import DOMPurify from 'dompurify';

/**
 * Sanitizes HTML content to prevent XSS attacks while preserving safe formatting
 * 
 * @param html - The HTML string to sanitize
 * @param strict - Whether to use strict sanitization (minimal tags allowed)
 * @returns Sanitized HTML string
 */
export const sanitizeHtml = (html: string, strict: boolean = false): string => {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // Handle the 'N/A' case specifically
  if (html === 'N/A') {
    return html;
  }

  try {
    if (strict) {
      // Strict mode: only allow basic formatting
      return DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u'],
        ALLOWED_ATTR: ['class'],
        FORBID_ATTR: ['style', 'id'],
        KEEP_CONTENT: true
      });
    } else {
      // Standard mode: allow more formatting but still secure
      return DOMPurify.sanitize(html, {
        ALLOWED_TAGS: [
          'p', 'div', 'span', 'br', 'strong', 'b', 'em', 'i', 'u', 
          'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
          'ul', 'ol', 'li',
          'a', 'img',
          'blockquote', 'pre', 'code',
          'table', 'thead', 'tbody', 'tr', 'td', 'th'
        ],
        ALLOWED_ATTR: [
          'class', 'id', 'style',
          'href', 'target', 'rel',
          'src', 'alt', 'width', 'height',
          'colspan', 'rowspan'
        ],
        ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.-]+(?:[^a-z+.-:]|$))/i,
        FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
        FORBID_TAGS: ['script', 'object', 'embed', 'form', 'input', 'textarea', 'select', 'button'],
        KEEP_CONTENT: true
      });
    }
  } catch (error) {
    console.error('HTML sanitization failed:', error);
    // Fallback: return empty string if sanitization fails
    return '';
  }
};

/**
 * Helper function to wrap nodes in a span
 */
const wrapNodesInSpan = (nodes: Node[], container: Element) => {
  if (nodes.length === 0) return;
  
  const span = document.createElement('span');
  span.style.display = 'inline';
  
  // Insert span before the first node
  container.insertBefore(span, nodes[0]);
  
  // Move all nodes into the span
  nodes.forEach(node => {
    span.appendChild(node);
  });
};

/**
 * Sanitizes HTML and applies custom CSS classes to elements with advanced text wrapping
 * This preserves the original complex styling logic while adding sanitization
 * 
 * @param html - The HTML string to sanitize
 * @param styles - CSS modules object containing class names
 * @param strict - Whether to use strict sanitization
 * @returns Sanitized HTML string with applied styles and proper text wrapping
 */
export const sanitizeAndStyleHtml = (
  html: string, 
  styles: { [key: string]: string }, 
  strict: boolean = false
): string => {
  const sanitizedHtml = sanitizeHtml(html, strict);
  
  if (!sanitizedHtml || sanitizedHtml === 'N/A') {
    return sanitizedHtml;
  }

  // Create a temporary DOM element to manipulate the sanitized HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = sanitizedHtml;

  // Apply styles to headings (original: h2 -> fieldLabel)
  if (styles.fieldLabel) {
    tempDiv.querySelectorAll('h2').forEach(h2 => {
      h2.className = styles.fieldLabel;
    });
  }

  // Apply styles to content elements (original logic preserved)
  if (styles.fieldValue) {
    tempDiv.querySelectorAll('p, div, li, ul, ol').forEach(element => {
      const existingClasses = element.className ? element.className + ' ' : '';
      element.className = existingClasses + styles.fieldValue;
    });
  }

  // Wrap text content in spans to ensure proper inline flow
  // This preserves the original complex text wrapping logic
  const childNodes = Array.from(tempDiv.childNodes);
  const nodesToWrap: Node[] = [];
  
  childNodes.forEach(child => {
    if (child.nodeType === Node.TEXT_NODE) {
      // Text node - needs wrapping
      nodesToWrap.push(child);
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      const element = child as Element;
      const tagName = element.tagName.toLowerCase();
      
      // If it's not a block element, it might need wrapping
      if (!['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'p', 'div', 'br'].includes(tagName)) {
        nodesToWrap.push(child);
      }
    }
  });

  // Group consecutive nodes that need wrapping (original logic preserved)
  if (nodesToWrap.length > 0) {
    let currentGroup: Node[] = [];
    let lastIndex = -1;

    childNodes.forEach((child, index) => {
      if (nodesToWrap.includes(child)) {
        if (lastIndex === -1 || index === lastIndex + 1) {
          currentGroup.push(child);
          lastIndex = index;
        } else {
          // Wrap previous group
          if (currentGroup.length > 0) {
            wrapNodesInSpan(currentGroup, tempDiv);
          }
          currentGroup = [child];
          lastIndex = index;
        }
      } else {
        // Not a node to wrap, finish current group
        if (currentGroup.length > 0) {
          wrapNodesInSpan(currentGroup, tempDiv);
          currentGroup = [];
          lastIndex = -1;
        }
      }
    });

    // Wrap remaining group
    if (currentGroup.length > 0) {
      wrapNodesInSpan(currentGroup, tempDiv);
    }
  }

  // Ensure bold and strong tags remain inline (original logic preserved)
  tempDiv.querySelectorAll('b, strong').forEach(element => {
    (element as HTMLElement).style.display = 'inline';
    (element as HTMLElement).style.whiteSpace = 'normal';
  });

  return tempDiv.innerHTML;
};

/**
 * Checks if a string contains potentially dangerous HTML
 * This can be used for logging/monitoring purposes
 * 
 * @param html - The HTML string to check
 * @returns True if potentially dangerous content was found
 */
export const containsDangerousHtml = (html: string): boolean => {
  if (!html || typeof html !== 'string') {
    return false;
  }

  const dangerousPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+\s*=/i, // Event handlers like onclick, onload, etc.
    /<iframe/i,
    /<object/i,
    /<embed/i,
    /<form/i,
    /data:text\/html/i
  ];

  return dangerousPatterns.some(pattern => pattern.test(html));
};
